import Styles from './StartDatePostFrequencyField.module.scss'
import { ChevronRight, ChevronRightSmall } from 'blaze-ui/icons/24'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Calendar2 } from 'blaze-ui/icons'
import { ExternalIntegrationItem } from 'components/ExternalIntegrationSelect/ExternalIntegrationItem'
import { ScheduleDescription } from '../ScheduleDescription/ScheduleDescription'
import { StartDatePostFrequencyFieldProps } from './Types'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { Pressable } from '@react-aria/interactions'
import { Field } from 'components/Wizards/Field/Field'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { PostingCadence } from '../../ScheduleContent'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import {
  DayAndTime,
  StartDateAndPostFrequencyModal,
  TimeOfDay,
} from '../StartDateAndPostFrequency/StartDateAndPostFrequencyModal'
import { StartDateAndPostFrequencyDrawer } from '../StartDateAndPostFrequency/StartDateAndPostFrequencyDrawer'
import { useModals } from 'almanac-ui/Modal'
import { Dayjs } from 'dayjs'
import { daysToPostingCadence } from 'components/Wizards/utils/postScheduling'
import { useState } from 'react'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { generatePath } from 'react-router-dom'
import { BLAZE_HOME_PATH } from 'paths'
import { openWindowSafely } from 'hooks/useOpenWindowSafely'

const StartDatePostFrequencyField = observer(
  ({
    initialPostingCadence,
    initialStartDate,
    onScheduleChange,
    externalIntegrationType,
    externalIntegration,
  }: StartDatePostFrequencyFieldProps) => {
    const { isMobile } = useResponsiveDesign()
    const { drawersStore } = useRootStore()
    const { openModal } = useModals()
    const [postingCadence, setPostingCadence] = useState(initialPostingCadence)
    const [startDate, setStartDate] = useState(initialStartDate)
    const [customDays, setCustomDays] = useState<DayAndTime[]>([])
    const { data, isFetched } = useExternalIntegrations()
    const workspaceId = useCurrentWorkspaceId()

    const openIntegrationsInNewTab = () => {
      const path = generatePath(BLAZE_HOME_PATH, { workspaceId })
      openWindowSafely(`${path}?modal=integrations`)
    }

    const showConnectIntegrationButton =
      isFetched &&
      externalIntegrationType &&
      !data?.external_integrations.some((i) => i.provider === externalIntegrationType)

    const handleScheduleChange = (schedule: { startDate: Dayjs; daysAndTimes: DayAndTime[]; isCustom: boolean }) => {
      if (schedule.isCustom) {
        setCustomDays(schedule.daysAndTimes)
        setPostingCadence(PostingCadence.Custom)
      } else {
        const days = schedule.daysAndTimes.map((dayAndTime) => dayAndTime.day)
        setPostingCadence(daysToPostingCadence(days))
      }
      setStartDate(schedule.startDate)
      onScheduleChange(schedule)
    }

    const openStartDateAndPostFrequency = () => {
      let initialDays: DayAndTime[] = []
      const dayNumbersToDaysAndTimes = (days: number[]) => {
        return days.map((day: number): DayAndTime => {
          return { day, time: { ofDay: TimeOfDay.Random } }
        })
      }
      switch (postingCadence) {
        case PostingCadence.Once:
          initialDays = [{ day: startDate.day(), time: { ofDay: TimeOfDay.Random } }]
          break
        case PostingCadence.Twice:
          initialDays = dayNumbersToDaysAndTimes([2, 4])
          break
        case PostingCadence.Thrice:
          initialDays = dayNumbersToDaysAndTimes([1, 3, 5])
          break
        case PostingCadence.Weekdays:
          initialDays = dayNumbersToDaysAndTimes([1, 2, 3, 4, 5])
          break
        case PostingCadence.Everyday:
          initialDays = dayNumbersToDaysAndTimes([0, 1, 2, 3, 4, 5, 6])
          break
        case PostingCadence.Custom:
          initialDays = customDays
      }

      if (isMobile) {
        drawersStore.openDrawer(StartDateAndPostFrequencyDrawer, {
          onScheduleChange: handleScheduleChange,
          initialStartDate: startDate,
          initialDays,
        })
      } else {
        openModal(
          StartDateAndPostFrequencyModal,
          {
            onScheduleChange: handleScheduleChange,
            initialStartDate: startDate,
            initialDays,
            initialCadence: postingCadence,
          },
          { newStack: true },
        )
      }
    }

    return (
      <Field label="Start date and post frequency" isRequired>
        <ResponsiveDesign.Mobile>
          <Pressable onPress={openStartDateAndPostFrequency}>
            <div className={Styles.startDateAndPostFrequencyButtonContainer}>
              <div className={Styles.titleAndDescription}>
                <div className={Styles.iconAndTitle}>
                  <Calendar2 />
                  <Heading level={5}>{startDate.format('ddd, MMM D')} </Heading>
                </div>
                <Text color="var(--dark-60)">
                  <ScheduleDescription postingCadence={postingCadence} customDays={customDays} startDate={startDate} />
                </Text>
              </div>
              <ChevronRight />
            </div>
          </Pressable>
        </ResponsiveDesign.Mobile>
        <ResponsiveDesign.Desktop>
          <div className={Styles.controls}>
            <div className={Styles.startDateButtonContainer}>
              <Button size="lg" variant="secondary" onPress={openStartDateAndPostFrequency} endIcon={ChevronRightSmall}>
                <div className={Styles.scheduleDescription}>
                  <span>{startDate.format('ddd, MMM D')}</span>
                  <div className={Styles.divider}></div>
                  <span className={Styles.lightText}>
                    <ScheduleDescription
                      postingCadence={postingCadence}
                      customDays={customDays}
                      startDate={startDate}
                    />
                  </span>
                </div>
              </Button>
            </div>
            {showConnectIntegrationButton && (
              <Button variant="secondary" onPress={openIntegrationsInNewTab}>
                Connect Account to Auto-Post
              </Button>
            )}
            {externalIntegration && (
              <div className={Styles.integrationContainer}>
                <ExternalIntegrationItem
                  nameClassName={Styles.integrationName}
                  externalIntegration={externalIntegration}
                  renderPlatformIcon
                />
              </div>
            )}
          </div>
        </ResponsiveDesign.Desktop>
      </Field>
    )
  },
)

export { StartDatePostFrequencyField }
